import { useQuery, UseQueryResult } from '@tanstack/react-query'

import { useAppSelector } from 'common/hooks/redux'

import { CategoryDetailsResponse, getCategoryDetails } from '../api'
import { keys } from '../queryKeys'

export type Category = {
  id: string
  name: string | undefined
  description: string | undefined
  ccName: string
  ccnPath: string[]
}

const useGetCategoryDetailsQuery = ({
  catalogId,
  region,
  ccnPath,
}: {
  catalogId: string | undefined
  region: string | undefined
  ccnPath: string | undefined
}): UseQueryResult<Category, unknown> => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const getCategoryDetailsQuery = useQuery({
    queryKey: keys.categoryDetails({ accountId, region, catalogId, ccnPath }),

    queryFn: () =>
      getCategoryDetails({
        accountId,
        region: region!,
        catalogId: catalogId!,
        ccnPath: ccnPath!,
      }),
    enabled: !!accountId && !!catalogId && !!ccnPath && !!region,
    select: (data: CategoryDetailsResponse) => ({
      id: data.id,
      ccName: data.cc_name,
      ccnPath: data.ccn_path,
      name: data.name ?? undefined,
      description: data.description ?? undefined,
    }),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: {
      errorMessage: 'The category details could not be loaded',
    },
  })

  return getCategoryDetailsQuery
}

export default useGetCategoryDetailsQuery
